import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { VideoFormat } from '@hyvay/video-engine';
import { videoService, errorText, type VideoConfig, type VideoListItem, type VideoRender } from '../../services/video.service';
import { BrandBtn, GhostBtn, Icons, Spinner, readTheme } from '../../components/studio/studioUi';
import { SEOHead } from '../../components/seo/SEOHead';
import { useToast } from '../../components/common/Toast';
import { FONT_MONO, FONT_SANS, FONT_SERIF } from '../landing/landingData';
import { BetaChip, FORMAT_OPTIONS, Segmented, fieldLabel } from './videoUi';
import { AttachedChips, MediaPicker } from './MediaPicker';
import { VideoWorkspace } from './VideoWorkspace';
import { RenderViewer } from './RenderActions';
import { ScheduleDialog } from './ScheduleDialog';
import type { MediaAsset } from '../../services/studio.service';
import '../landing/landing.css';

/** /video — inline "new video" composer + the user's projects, finished MP4s and media. */
export const VideoListView: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [cfg, setCfg] = useState<VideoConfig | null>(null);
    const [items, setItems] = useState<VideoListItem[] | null>(null);
    const [media, setMedia] = useState<MediaAsset[] | null>(null);
    const [viewing, setViewing] = useState<VideoListItem | null>(null);
    /** Project being posted from Hasil video; its renders (all versions) come from the full project. */
    const [posting, setPosting] = useState<{ item: VideoListItem; renders: VideoRender[] } | null>(null);

    const openPost = async (item: VideoListItem) => {
        try {
            setPosting({ item, renders: (await videoService.get(item.id)).renders });
        } catch (e) {
            toast.error(errorText(e));
        }
    };
    // The dialog can start a 9:16 render: follow it until it finishes.
    const postingId = posting?.item.id;
    const postingRender = !!posting?.renders.some((r) => r.status === 'QUEUED' || r.status === 'RENDERING');
    useEffect(() => {
        if (!postingId || !postingRender) return;
        const t = window.setInterval(() => {
            videoService.get(postingId).then((v) => setPosting((p) => (p && p.item.id === postingId ? { ...p, renders: v.renders } : p))).catch(() => {});
        }, 3000);
        return () => window.clearInterval(t);
    }, [postingId, postingRender]);

    useEffect(() => {
        videoService.config().then(setCfg).catch(() => setCfg(null));
        videoService.list().then(setItems).catch(() => setItems([]));
        videoService.media().then(setMedia).catch(() => setMedia([]));
    }, []);

    const focusComposer = () => {
        const el = document.getElementById('video-composer');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.querySelector('textarea')?.focus({ preventScroll: true });
    };

    if (cfg && !cfg.enabled) {
        return (
            <div className="hv-root" data-hv-theme={readTheme()} style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--hv-desk)', fontFamily: FONT_SANS, color: 'var(--hv-ink)' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_SERIF, fontSize: 36 }}>Video maker belum aktif</div>
                    <p style={{ color: 'var(--hv-muted)' }}>Fitur ini masih tahap uji coba.</p>
                    <GhostBtn onClick={() => navigate('/')}>Kembali</GhostBtn>
                </div>
            </div>
        );
    }

    return (
        <div className="hv-root" data-hv-theme={readTheme()} style={{ minHeight: '100vh', background: 'var(--hv-desk)', fontFamily: FONT_SANS, color: 'var(--hv-ink)', padding: '28px 20px 60px' }}>
            <SEOHead title="Video maker" noindex={true} />
            <div style={{ maxWidth: 1120, margin: '0 auto' }}>
                <button type="button" onClick={() => navigate('/')} style={{ border: 0, background: 'transparent', color: 'var(--hv-muted)', cursor: 'pointer', fontSize: 13.5, padding: 0 }}>← Beranda</button>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, marginTop: 18, flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, letterSpacing: '0.2em', color: 'var(--hv-leaf)' }}>VIDEO MAKER</span>
                            <BetaChip />
                        </div>
                        <h1 style={{ margin: '10px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 52, lineHeight: 1, letterSpacing: '-0.02em' }}>
                            Video promosi, <i>sekali chat.</i>
                        </h1>
                        <p style={{ margin: '12px 0 0', color: 'var(--hv-body)', fontSize: 15.5, maxWidth: 560 }}>
                            Ceritakan usahamu atau pilih situs HyVay-mu. AI menyusun scene, teks, musik, dan narasinya. Render jadi MP4, lalu posting atau jadwalkan langsung ke media sosial.
                        </p>
                    </div>
                    <GhostBtn onClick={() => navigate('/video/schedule')}>Jadwal posting & akun</GhostBtn>
                </div>

                <NewVideoComposer cfg={cfg} onCreated={(id, firstMessage) => navigate(`/video/${id}`, { state: { firstMessage } })} onError={(m) => toast.error(m)} />

                <VideoWorkspace projects={items} media={media} onOpenProject={(id) => navigate(`/video/${id}`)} onCreate={focusComposer} onViewRender={setViewing} onPostRender={openPost} />
            </div>
            <RenderViewer render={viewing?.lastDone ?? null} title={viewing?.name ?? ''} onClose={() => setViewing(null)} onPost={() => { const v = viewing; setViewing(null); if (v) openPost(v); }} />
            {posting && (
                <ScheduleDialog
                    open
                    onClose={() => setPosting(null)}
                    videoName={posting.item.name}
                    renders={posting.renders}
                    initialRenderId={posting.item.lastDone?.id}
                    renderCost={cfg?.renderCost ?? 10}
                    onRenderPortrait={() => {
                        const id = posting.item.id;
                        videoService
                            .render(id, 'portrait')
                            .then((r) => {
                                setPosting((p) => (p && p.item.id === id ? { ...p, renders: [r, ...p.renders] } : p));
                                toast.info('Render 9:16 dimulai.');
                            })
                            .catch((e) => toast.error(errorText(e)));
                    }}
                    onScheduled={() => {}}
                />
            )}
        </div>
    );
};

/**
 * Inline "new video" composer, laid out like the landing hero composer: mode toggle,
 * an optional site picker, the prompt, attachments, then format + submit in one row.
 * `cfg` is null while the config is still loading; submitting stays disabled until then.
 */
const NewVideoComposer: React.FC<{ cfg: VideoConfig | null; onCreated: (id: string, firstMessage: string) => void; onError: (m: string) => void }> = ({ cfg, onCreated, onError }) => {
    const [mode, setMode] = useState<'idea' | 'site'>('idea');
    const [format, setFormat] = useState<VideoFormat>('landscape');
    const [prompt, setPrompt] = useState('');
    const [appId, setAppId] = useState<string>('');
    const [files, setFiles] = useState<MediaAsset[]>([]);
    const [picker, setPicker] = useState(false);
    const [busy, setBusy] = useState(false);

    const canSubmit = !!cfg && !busy && (mode === 'idea' ? prompt.trim().length > 5 || files.length > 0 : !!appId);
    const submit = async () => {
        setBusy(true);
        try {
            const { id } = await videoService.create({ format, applicationId: mode === 'site' ? appId : undefined, assetIds: files.map((f) => f.assetId) });
            const withFiles = files.length ? ' Pakai gambar & video yang saya lampirkan.' : '';
            const first = mode === 'site'
                ? `Buatkan video promosi dari situs saya.${withFiles}${prompt.trim() ? ` ${prompt.trim()}` : ''}`
                : prompt.trim()
                    ? `${prompt.trim()}${withFiles}`
                    : 'Buatkan video promosi dari gambar & video yang saya lampirkan.';
            onCreated(id, first);
        } catch (e) {
            onError(errorText(e));
            setBusy(false);
        }
    };
    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSubmit) { e.preventDefault(); submit(); }
    };

    return (
        <div id="video-composer" className="hv-composer" style={{ marginTop: 28, padding: '14px 16px 12px', borderRadius: 24, background: 'var(--hv-card)', border: '1.5px solid var(--hv-line)', boxShadow: '0 34px 60px -34px var(--hv-shadow)' }}>
            <Segmented value={mode} onChange={setMode} options={[{ value: 'idea', label: 'Dari ide' }, { value: 'site', label: 'Dari situs HyVay' }]} />
            {mode === 'site' && (
                <div style={{ marginTop: 14 }}>
                    <div style={fieldLabel}>Pilih situs</div>
                    {cfg?.apps.length === 0 && <div style={{ color: 'var(--hv-muted)', fontSize: 13.5 }}>Kamu belum punya situs di HyVay.</div>}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, maxHeight: 260, overflow: 'auto' }}>
                        {cfg?.apps.map((a) => (
                            <button key={a.id} type="button" onClick={() => setAppId(a.id)} style={{ textAlign: 'left', padding: 8, borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--hv-ink)', background: 'var(--hv-card)', border: `2px solid ${appId === a.id ? 'var(--brand,#13422C)' : 'var(--hv-line)'}` }}>
                                <div style={{ height: 78, borderRadius: 9, background: a.thumbnailUrl ? `center/cover url("${a.thumbnailUrl}")` : 'var(--hv-page-2)' }} />
                                <div style={{ marginTop: 8, fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</div>
                                <div style={{ fontSize: 11.5, color: a.published ? 'var(--hv-leaf)' : 'var(--hv-muted)' }}>{a.published ? 'Sudah online' : 'Draf'}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={onKeyDown}
                aria-label={mode === 'idea' ? 'Ceritakan videonya' : 'Catatan tambahan'}
                placeholder={mode === 'idea' ? 'Ceritakan videonya. Contoh: video promosi 30 detik untuk kedai kopi saya di Bandung, promo beli 2 gratis 1 sampai akhir bulan, pakai narasi.' : 'Catatan tambahan (opsional). Contoh: tonjolkan promo bulan ini, pakai narasi.'}
                style={{ display: 'block', width: '100%', boxSizing: 'border-box', minHeight: 96, marginTop: 12, resize: 'none', border: 0, outline: 'none', background: 'transparent', fontSize: 16.5, lineHeight: 1.6, color: 'var(--hv-ink)', fontFamily: 'inherit', padding: 2 }}
            />
            {files.length > 0 && <div style={{ marginTop: 6 }}><AttachedChips items={files} onRemove={(id) => setFiles((f) => f.filter((x) => x.assetId !== id))} /></div>}
            <div className="hv-row-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <button type="button" onClick={() => setPicker(true)} title="AI akan memotong & menyusunnya jadi scene." style={{ height: 36, padding: '0 14px', borderRadius: 999, border: '1px dashed var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    + Gambar & video
                </button>
                {cfg && <span style={{ fontSize: 12, color: 'var(--hv-muted)' }}>{cfg.chatCost} credit per pesan · render MP4 {cfg.renderCost} credit</span>}
                <div style={{ flex: 1 }} />
                <Segmented value={format} onChange={setFormat} options={FORMAT_OPTIONS} />
                <BrandBtn data-track="video_create" onClick={submit} disabled={!canSubmit}>{busy ? <Spinner light /> : <>Buat video <Icons.arrow size={14} /></>}</BrandBtn>
            </div>
            <MediaPicker open={picker} onClose={() => setPicker(false)} exclude={files.map((f) => f.assetId)} onDone={(add) => setFiles((f) => [...f, ...add])} />
        </div>
    );
};
